import { useCallback, useEffect, useState } from 'react';
import { rupiah } from '@santap/shared';
import type { PaymentInput } from '@santap/shared';
import { api, GalatApi, GalatJaringan } from '../lib/api';
import { antrekan } from '../lib/outbox';
import { Bayar } from './Bayar';

type Tagihan = Awaited<ReturnType<typeof api.tagihanTerbuka>>[number];

/**
 * Tagihan yang menunggu dibayar di kasir.
 *
 * Pesanan dari kiosk dan QR meja yang dipilih "bayar di kasir" mendarat di
 * sini. Tanpa layar ini, alurnya buntu: layar dapur menampilkan "BELUM
 * DIBAYAR — tagih sekian", tapi tidak ada tempat untuk menagihnya.
 *
 * Membayar di sini melakukan dua hal sekaligus: mencatat uangnya, dan —
 * untuk pesanan yang ditahan — melepaskannya ke dapur. Pembayaran adalah
 * izin untuk mulai memasak.
 */
export function TagihanTerbuka({
  outletId, tutup, lunas,
}: { outletId: string; tutup: () => void; lunas: (pesan: string) => void }) {
  const [daftar, setDaftar] = useState<Tagihan[] | null>(null);
  const [galat, setGalat] = useState<string | null>(null);
  const [pilih, setPilih] = useState<any | null>(null);
  const [bayarBuka, setBayarBuka] = useState(false);

  const muat = useCallback(async () => {
    try {
      setDaftar(await api.tagihanTerbuka(outletId));
      setGalat(null);
    } catch (e) {
      setGalat(e instanceof GalatJaringan
        ? 'Tidak bisa menghubungi server. Daftar tagihan butuh internet — pesanan baru di kasir tetap bisa diproses offline.'
        : e instanceof GalatApi ? e.message : 'Gagal memuat tagihan');
    }
  }, [outletId]);

  useEffect(() => { void muat(); }, [muat]);

  async function buka(t: Tagihan) {
    try {
      setPilih(await api.rincianTagihan(t.id));
    } catch (e) {
      setGalat(e instanceof GalatApi ? e.message : 'Gagal memuat rincian');
    }
  }

  /**
   * Catat pembayaran untuk pesanan yang SUDAH ada di server.
   *
   * Beda dari alur kasir biasa: di sini hanya pembayarannya yang diantrekan,
   * bukan pesanannya — pesanan sudah tercatat saat pelanggan memesan di kiosk
   * atau di meja.
   */
  async function selesaikan(pembayaran: Omit<PaymentInput, 'orderId'>) {
    await antrekan('pembayaran', { ...pembayaran, orderId: pilih.id } as PaymentInput);
    setBayarBuka(false);
    setPilih(null);
    lunas(
      pilih.tertahan
        ? `${pilih.nomorStruk} lunas · pesanan dilepas ke dapur`
        : `${pilih.nomorStruk} lunas`,
    );
  }

  // --- rincian satu tagihan
  if (pilih) {
    if (bayarBuka) {
      return (
        <Bayar total={pilih.sisa} struk={pilih.nomorStruk} online={navigator.onLine}
               tutup={() => setBayarBuka(false)} selesai={selesaikan}
               siapkanPesananDaring={async () => pilih.id}
               selesaiQris={async () => {
                 setBayarBuka(false); setPilih(null);
                 lunas(`${pilih.nomorStruk} lunas lewat QRIS`);
               }} />
      );
    }
    return (
      <div className="tirai" onClick={(e) => e.target === e.currentTarget && setPilih(null)}>
        <div className="modal">
          <div className="mh">
            <div>
              <h2>{pilih.nomorStruk}</h2>
              <div className="sub">
                {labelKanal(pilih.channel)}
                {pilih.meja ? ` · meja ${pilih.meja}` : ''}
                {pilih.nomorAntrean ? ` · antrean ${pilih.nomorAntrean}` : ''}
              </div>
            </div>
            <button className="x" onClick={() => setPilih(null)} aria-label="Tutup">×</button>
          </div>
          <div className="mb">
            {pilih.tertahan && (
              <div className="galat" style={{
                background: 'var(--warn-soft)', color: 'var(--warn)',
              }}>
                Pesanan ini belum masuk dapur. Begitu dibayar, tiketnya langsung
                dikirim ke dapur.
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              {pilih.baris.map((b: any) => (
                <div className="tg-item" key={b.id}>
                  <span className="q">{b.qty}×</span>
                  <span style={{ flex: 1 }}>
                    {b.nama}
                    {b.modifier?.length > 0 && <div className="m">{b.modifier.join(' · ')}</div>}
                    {b.catatan && <div className="m">⚑ {b.catatan}</div>}
                  </span>
                  <span style={{ fontFamily: '"IBM Plex Mono",monospace' }}>{rupiah(b.total)}</span>
                </div>
              ))}
            </div>

            <div className="tg-rinci">
              <div className="r"><span>Subtotal</span><span>{rupiah(pilih.rincian.subtotal)}</span></div>
              {pilih.rincian.serviceCharge > 0 && (
                <div className="r"><span>Service charge</span><span>{rupiah(pilih.rincian.serviceCharge)}</span></div>
              )}
              <div className="r"><span>PB1</span><span>{rupiah(pilih.rincian.pb1)}</span></div>
              {pilih.rincian.pembulatan !== 0 && (
                <div className="r"><span>Pembulatan</span><span>{rupiah(pilih.rincian.pembulatan)}</span></div>
              )}
              {pilih.terbayar > 0 && (
                <div className="r" style={{ color: 'var(--ok)' }}>
                  <span>Sudah dibayar</span><span>−{rupiah(pilih.terbayar)}</span>
                </div>
              )}
              <div className="r besar"><span>Harus ditagih</span><span>{rupiah(pilih.sisa)}</span></div>
            </div>

            {pilih.namaPelanggan && (
              <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>
                Atas nama <b style={{ color: 'var(--ink)' }}>{pilih.namaPelanggan}</b>
              </p>
            )}
          </div>
          <div className="mf">
            <button className="tbl samar" onClick={() => setPilih(null)}>Kembali</button>
            <button className="tbl utama" onClick={() => setBayarBuka(true)}>
              Tagih {rupiah(pilih.sisa)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- daftar
  return (
    <div className="tirai" onClick={(e) => e.target === e.currentTarget && tutup()}>
      <div className="modal">
        <div className="mh">
          <div>
            <h2>Tagihan menunggu</h2>
            <div className="sub">{daftar?.length ?? 0} belum lunas</div>
          </div>
          <button className="x" onClick={tutup} aria-label="Tutup">×</button>
        </div>
        <div className="mb">
          {galat && <div className="galat">{galat}</div>}
          {!daftar ? (
            <div className="proses"><div className="putar" /></div>
          ) : daftar.length === 0 ? (
            <div className="kosong" style={{ padding: '36px 20px' }}>
              <b>Tidak ada tagihan menunggu</b>
              Pesanan dari kiosk dan QR meja yang memilih bayar di kasir akan muncul di sini.
            </div>
          ) : (
            <div className="tg-list">
              {daftar.map((t) => (
                <button className="tg" key={t.id} onClick={() => void buka(t)}>
                  {t.nomorAntrean
                    ? <div className="antrean">{t.nomorAntrean}</div>
                    : <div className="antrean" style={{
                        background: 'var(--sunk)', color: 'var(--muted)', fontSize: 13,
                      }}>{t.meja ?? '—'}</div>}
                  <div className="kiri">
                    <div className="no">{t.nomorStruk}</div>
                    <div className="sub">
                      {t.tertahan && <span className="tg-tag tertahan">belum dimasak</span>}
                      <span className="tg-tag kanal">{labelKanal(t.channel)}</span>
                      {t.jumlahItem} item
                      {t.namaPelanggan ? ` · ${t.namaPelanggan}` : ''}
                    </div>
                  </div>
                  <div className="amt">{rupiah(t.sisa)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mf">
          <button className="tbl samar" style={{ flex: 1 }} onClick={() => void muat()}>
            Segarkan
          </button>
        </div>
      </div>
    </div>
  );
}

function labelKanal(c: string) {
  return c === 'qr_meja' ? 'QR meja' : c === 'kiosk' ? 'Kiosk'
    : c === 'online' ? 'Online' : 'Kasir';
}
